/**
 * Keeps the previous height for a moment when `resetKey` changes (DEV-103).
 *
 * A detail area beside a list is rebuilt for every selection and loads its
 * parts anew; for that moment it is short and the dialog collapses and opens
 * again. This wrapper holds the last height until the new content is there,
 * then lets it follow its content.
 */
import { useLayoutEffect, useRef, type ReactNode } from 'react';

const HOLD_MS = 700;

export default function KeepHeight({ resetKey, className, children }: Readonly<{
  resetKey: string | number | null;
  className?: string;
  children: ReactNode;
}>): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const height = useRef(0);
  const first = useRef(true);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(() => {
      if (!element.style.minHeight) height.current = element.offsetHeight;
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Runs after the new content is in the DOM but before it is painted.
  useLayoutEffect(() => {
    const element = ref.current;
    if (first.current || !element) {
      first.current = false;
      return undefined;
    }
    element.style.minHeight = `${height.current}px`;
    const timer = window.setTimeout(() => {
      element.style.minHeight = '';
      height.current = element.offsetHeight;
    }, HOLD_MS);
    return () => {
      window.clearTimeout(timer);
      element.style.minHeight = '';
    };
  }, [resetKey]);

  return <div ref={ref} className={className}>{children}</div>;
}
